import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LangContext";
import UsageBadge from "@/components/UsageBadge";
import { Menu, X } from "lucide-react";

export default function Header({ variant = "default" }) {
  const { user, logout } = useAuth();
  const { lang, toggle, t } = useLang();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();

  // Close menu when route changes
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while menu is open (mobile only)
  useEffect(() => {
    if (menuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [menuOpen]);

  const navLinks = user
    ? [
        { id: "nav-dashboard", to: "/app", label: t("nav_dashboard") },
        { id: "nav-trends", to: "/app/trends", label: t("trends_nav") },
        { id: "nav-press-kit", to: "/app/press-kit", label: t("nav_my_press_kit") },
        { id: "nav-usage", to: "/app/usage", label: lang === "es" ? "Uso" : "Usage" },
        { id: "nav-billing", to: "/app/billing", label: t("nav_billing") },
      ]
    : [];

  return (
    <header
      data-testid="site-header"
      className={`w-full border-b border-[#171717] ${variant === "inverted" ? "bg-[#0A0A0A] text-white" : "bg-white text-[#0A0A0A]"}`}
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-5 flex items-center justify-between gap-6">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-3 group">
          <div className="w-7 h-7 bg-[#D90429] flex items-center justify-center font-mono-tech text-white text-xs font-bold">
            G
          </div>
          <div className="leading-none">
            <div className="font-serif-display text-xl md:text-2xl font-bold tracking-tight">
              GalascioDAS
            </div>
            <div className="font-mono-tech text-[10px] tracking-[0.25em] uppercase opacity-70">
              Discourse Analysis System
            </div>
          </div>
        </Link>

        <nav className="flex items-center gap-2 md:gap-4 font-mono-tech text-xs uppercase tracking-widest">
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="px-3 py-2 border border-current hover:bg-[#D90429] hover:text-white hover:border-[#D90429] transition-colors"
            aria-label="toggle language"
          >
            {lang === "es" ? "ES · EN" : "EN · ES"}
          </button>
          {user ? (
            <>
              {/* Desktop nav links */}
              {navLinks.map((l) => (
                <Link
                  key={l.id}
                  data-testid={l.id}
                  to={l.to}
                  className="px-3 py-2 hidden md:inline-block hover:text-[#D90429]"
                >
                  {l.label}
                </Link>
              ))}
              <span className="hidden md:inline-flex">
                <UsageBadge variant={variant} />
              </span>
              <span className="hidden md:inline-block opacity-60 text-[10px]">
                {user.email}
              </span>
              <button
                data-testid="nav-logout"
                onClick={logout}
                className="hidden md:inline-block px-4 py-2 border border-current hover:bg-current hover:text-white transition-colors"
              >
                {t("nav_logout")}
              </button>

              {/* Mobile usage badge (always visible) + hamburger */}
              <span className="md:hidden inline-flex">
                <UsageBadge variant={variant} />
              </span>
              <button
                data-testid="mobile-menu-toggle"
                onClick={() => setMenuOpen((v) => !v)}
                aria-label={menuOpen ? "close menu" : "open menu"}
                aria-expanded={menuOpen}
                aria-controls="mobile-nav-panel"
                className="md:hidden p-2 border border-current hover:bg-[#D90429] hover:text-white hover:border-[#D90429] transition-colors"
              >
                {menuOpen ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
              </button>
            </>
          ) : (
            <>
              <Link
                data-testid="nav-pricing"
                to="/pricing"
                className="px-3 py-2 hidden sm:inline-block hover:text-[#D90429]"
              >
                {t("nav_pricing")}
              </Link>
              <Link
                data-testid="nav-docs"
                to="/docs"
                className="px-3 py-2 hidden sm:inline-block hover:text-[#D90429]"
              >
                {t("docs_nav")}
              </Link>
              <Link
                data-testid="nav-login"
                to="/login"
                className="px-3 py-2 hover:text-[#D90429]"
              >
                {t("nav_login")}
              </Link>
              <Link
                data-testid="nav-register"
                to="/register"
                className="px-4 py-2 bg-[#0A0A0A] text-white border border-[#0A0A0A] hover:bg-[#D90429] hover:border-[#D90429] transition-colors"
              >
                {t("nav_register")}
              </Link>
            </>
          )}
        </nav>
      </div>

      {/* Mobile slide-down panel */}
      {user && menuOpen && (
        <div
          id="mobile-nav-panel"
          data-testid="mobile-nav-panel"
          role="dialog"
          aria-modal="true"
          aria-label="navigation menu"
          className="md:hidden fixed inset-x-0 top-[81px] bottom-0 z-40 bg-white text-[#0A0A0A] border-t border-[#171717] overflow-y-auto"
        >
          <nav className="flex flex-col p-4 gap-1 font-mono-tech text-sm uppercase tracking-widest">
            {navLinks.map((l) => (
              <Link
                key={`m-${l.id}`}
                data-testid={`mobile-${l.id}`}
                to={l.to}
                onClick={() => setMenuOpen(false)}
                className="px-4 py-4 border border-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white"
              >
                · {l.label}
              </Link>
            ))}
            <div className="border-t border-[#E5E5E5] mt-4 pt-4 px-2">
              <div className="text-[10px] tracking-widest text-[#A3A3A3] mb-2 normal-case">
                {user.email}
              </div>
              <button
                data-testid="mobile-nav-logout"
                onClick={() => {
                  setMenuOpen(false);
                  logout();
                }}
                className="w-full px-4 py-3 border border-[#D90429] text-[#D90429] hover:bg-[#D90429] hover:text-white"
              >
                {t("nav_logout")}
              </button>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
}
